import { useSyncExternalStore } from "react";

interface ImpersonationState {
	isImpersonating: boolean;
	userId: string | null;
	userName: string | null;
	endImpersonation: () => void;
}

const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
	listeners.add(callback);
	return () => listeners.delete(callback);
}

function notifyListeners() {
	for (const listener of listeners) listener();
}

function getSnapshot(): string | null {
	if (typeof window === "undefined") return null;
	return sessionStorage.getItem("impersonation_user");
}

function getServerSnapshot(): string | null {
	return null;
}

export function setImpersonation(
	token: string,
	expires: string,
	userId: string | null,
	userName: string,
) {
	sessionStorage.setItem(
		"impersonation_token",
		JSON.stringify({ token, expires }),
	);
	sessionStorage.setItem(
		"impersonation_user",
		JSON.stringify({ userId, userName }),
	);
	notifyListeners();
}

export function clearImpersonation() {
	sessionStorage.removeItem("impersonation_token");
	sessionStorage.removeItem("impersonation_user");
	notifyListeners();
}

function endImpersonation() {
	clearImpersonation();

	try {
		window.close();
	} catch {
		// window.close() may be blocked by the browser
	}
}

// Clear impersonation tokens when the tab/window is closed
if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", () => {
		clearImpersonation();
	});
}

export function useImpersonation(): ImpersonationState {
	const raw = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

	if (!raw) {
		return {
			isImpersonating: false,
			userId: null,
			userName: null,
			endImpersonation,
		};
	}

	try {
		const { userId, userName } = JSON.parse(raw);

		// Also check if the token is still valid
		const tokenRaw = sessionStorage.getItem("impersonation_token");
		if (tokenRaw) {
			const { expires } = JSON.parse(tokenRaw);
			if (new Date(expires) <= new Date()) {
				return {
					isImpersonating: false,
					userId: null,
					userName: null,
					endImpersonation,
				};
			}
		}

		return {
			isImpersonating: true,
			userId,
			userName,
			endImpersonation,
		};
	} catch {
		return {
			isImpersonating: false,
			userId: null,
			userName: null,
			endImpersonation,
		};
	}
}
